// ccp/src/lib/softphoneErrors.js
//
// Incident 2026-09-14 (docs/ops/incident-call-handling-2026-09-14.md), fix 2:
// the console subscribed to none of the Streams softphone events, so a voice
// accept whose WebRTC session never established looked identical to "nothing
// happened". This module is the PURE half: it turns a Streams SoftphoneError
// (or one of our own accept/contact failures) into a record the banner and
// the incoming screen can show, and keeps the last few in sessionStorage so
// they survive a reload and can be read after the fact.
//
// It does NOT try to fix or work around the media failure — that stays open
// until the Streams log reproduction (incident doc §3.6).

// connect.SoftphoneErrorTypes in Streams 2.25.1, plus three of our own.
export const FRIENDLY = {
  microphone_not_shared:
    'Your microphone is not available to the console. Check Chrome’s microphone permission for knktr-demo.my.connect.aws and agents.knktr.ai, then reload.',
  signalling_handshake_failure:
    'The voice connection to Amazon Connect could not be set up (signalling handshake failed). Calls cannot be answered until it recovers — reload the console if it persists.',
  signalling_connection_failure:
    'The voice connection to Amazon Connect could not be reached (signalling). Check the network, then reload the console.',
  ice_collection_timeout:
    'The voice connection to Amazon Connect timed out (ICE). Calls cannot be answered until it recovers — reload the console if it persists.',
  webrtc_error:
    'A WebRTC error stopped the voice connection. Reload the console.',
  realtime_communication_error:
    'A real-time communication error stopped the voice connection. Reload the console.',
  user_busy_error:
    'Amazon Connect reported the softphone as busy. Reload the console if you are not on a call.',
  unsupported_browser:
    'This browser is not supported for the softphone. Use current Chrome.',
  vdi_strategy_not_supported:
    'The virtual-desktop audio strategy is not supported here.',
  vdi_redir_not_supported:
    'Virtual-desktop audio redirection is not supported here.',
  accept_failed:
    'Amazon Connect refused the Accept. The call will keep ringing until the timeout — try again, or use the backup CCP.',
  // F7 (2026-10-15): Accept reported a failure but the call connected within the grace
  // window. Logged for diagnosis only; never shown as a banner.
  accept_late:
    'Amazon Connect reported the Accept as failed, but the call connected a moment later.',
  contact_error:
    'Amazon Connect put this contact into an error state on your session.',
  // 2026-09-23: the manual Clear used to fail silently (devtools only).
  clear_failed:
    'Clearing the missed contact failed — Amazon Connect refused it. Try again; the automatic clear runs within about three minutes, or use the backup CCP.',
  access_denied:
    'Amazon Connect denied the console access. Sign in to Connect again.',
  // Row 1 Transfer, Phase 1 (2026-09-28): voice cold transfer is
  // addConnection() then getAgentConnection().destroy() — two separate
  // Streams calls, so there are two distinct ways it can go wrong after the
  // new party is already on the call. Both stay visible until this exact
  // contact ends (never cleared by dismissing the banner alone) — see
  // ContactContext.jsx's CONTACT_ENDED case.
  transfer_partial:
    'The transfer target was connected, but Amazon Connect could not disconnect your own leg — you may still be on this call with them. Check your call now; use End if you are still connected.',
  transfer_timeout:
    'Amazon Connect did not respond to the transfer in time. It is not known whether the target was connected or your own leg was dropped — check your call now before trying again.',
  other:
    'The softphone reported a problem. Reload the console if calls do not connect.',
};

export const STORAGE_KEY = 'knktr.softphoneErrors';
export const KEEP = 20;

// Normalise whatever Streams (or we) hand over into one shape.
export function toRecord(errorType, err, at = Date.now()) {
  const type = String(errorType || err?.errorType || 'other').toLowerCase();
  const detail = [err?.errorMessage, err?.message, err?.endPointUrl]
    .map(x => (typeof x === 'string' ? x.trim() : ''))
    .filter(Boolean)
    .join(' ')
    .slice(0, 300);
  return {
    type,
    friendly: FRIENDLY[type] || FRIENDLY.other,
    detail: detail || null,
    at,
  };
}

// Append to sessionStorage (best effort; never throws).
export function remember(record) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const next = [...(Array.isArray(list) ? list : []), record].slice(-KEEP);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return [record];
  }
}

export function recall() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function fmtTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch { return ''; }
}
